import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { filesService } from "../services/files.service";
import { useSelectedColoc } from "../hooks/useSelectedColoc";
import "../styles/DropInvoicesPage.css";
import { getErrorMessage } from '../utils/error';

// PDF seulement : le serveur lit le texte du PDF, pas une image (ticket #13).
const ALLOWED_TYPES = ["application/pdf"];
const MIN_FILE_SIZE = 30 * 1024; // 30 KB

interface SelectedFile {
  file: File;
  error?: string;
}

const DropInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validateFile = (file: File): string | undefined => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Format non accepté : seuls les PDF sont lus. Une photo de facture ne l'est pas.";
    }
    if (file.size < MIN_FILE_SIZE) {
      return `Fichier trop petit (${(file.size / 1024).toFixed(1)} KB). Minimum 30 KB.`;
    }
    return undefined;
  };

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    setSuccessMessage(null);
    setErrorMessage(null);

    const filesToAdd: SelectedFile[] = Array.from(newFiles).map((file) => ({
      file,
      error: validateFile(file),
    }));

    setSelectedFiles((prev) => {
      // Avoid duplicates by name+size
      const existing = new Set(prev.map((f) => `${f.file.name}-${f.file.size}`));
      const unique = filesToAdd.filter((f) => !existing.has(`${f.file.name}-${f.file.size}`));
      return [...prev, ...unique];
    });
  }, []);

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  const validFiles = selectedFiles.filter((f) => !f.error);

  const handleUpload = async () => {
    if (!selectedColoc || validFiles.length === 0) return;

    setUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await filesService.uploadFilesToPending(
        validFiles.map((f) => f.file),
        selectedColoc.id
      );

      if (result.failed > 0) {
        const failedNames = result.results
          .filter((r) => !r.success)
          .map((r) => r.filename)
          .join(", ");
        setErrorMessage(`${result.failed} fichier(s) en erreur : ${failedNames}`);
      }

      if (result.succeeded > 0) {
        setSuccessMessage(
          `${result.succeeded} facture(s) deposee(s) avec succes. Elles seront traitees automatiquement dans les prochaines minutes.`
        );
        // Remove successfully uploaded files from the list
        const failedFilenames = new Set(
          result.results.filter((r) => !r.success).map((r) => r.filename)
        );
        setSelectedFiles((prev) =>
          prev.filter((f) => failedFilenames.has(f.file.name))
        );
      }
    } catch (err) {
      setErrorMessage(
        getErrorMessage(err, "Erreur lors de l'envoi des fichiers")
      );
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="drop-invoices-container">
      <div className="drop-invoices-header-section">
        <h2>Déposer des factures</h2>
        {selectedColoc && (
          <div className="selected-coloc-info">
            Colocation: <span className="coloc-name">{selectedColoc.name}</span>
          </div>
        )}
      </div>
      <p className="drop-invoices-description">
        Déposez vos factures ici pour qu'elles soient traitées automatiquement,
        exactement comme si vous les envoyiez par e-mail.
      </p>

      {successMessage && (
        <div className="drop-success-message">
          <span>{successMessage}</span>
          <button
            className="link-button"
            onClick={() => navigate("/pending-invoices")}
          >
            Voir les factures en attente &rarr;
          </button>
        </div>
      )}

      {errorMessage && <div className="drop-error-message">{errorMessage}</div>}

      {/* Drop zone */}
      <div
        className={`drop-zone ${isDragging ? "dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="drop-zone-icon">&#128196;</div>
        <div className="drop-zone-text">Glissez vos factures ici</div>
        <div className="drop-zone-subtext">
          ou cliquez pour sélectionner des fichiers
        </div>
        <div className="drop-zone-formats">PDF uniquement</div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="application/pdf"
          onChange={handleFileInputChange}
          className="drop-zone-input"
        />
      </div>

      {/* File list */}
      {selectedFiles.length > 0 && (
        <div className="file-list">
          <div className="file-list-header">
            <span>{selectedFiles.length} fichier(s) selectionne(s)</span>
            {selectedFiles.length > 0 && (
              <button
                className="clear-all-button"
                onClick={() => setSelectedFiles([])}
              >
                Tout retirer
              </button>
            )}
          </div>

          {selectedFiles.map((sf, index) => (
            <div
              key={`${sf.file.name}-${sf.file.size}-${index}`}
              className={`file-row ${sf.error ? "file-row-error" : ""}`}
            >
              <div className="file-info">
                <span className="file-icon">
                  {sf.file.type === "application/pdf" ? "📄" : "🖼️"}
                </span>
                <span className="file-name">{sf.file.name}</span>
                <span className="file-size">{formatFileSize(sf.file.size)}</span>
              </div>
              {sf.error && <div className="file-error-text">{sf.error}</div>}
              <button
                className="remove-file-button"
                onClick={() => removeFile(index)}
                title="Retirer ce fichier"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {selectedFiles.length > 0 && (
        <button
          className="upload-button"
          onClick={handleUpload}
          disabled={uploading || validFiles.length === 0}
        >
          {uploading
            ? "Envoi en cours..."
            : `Envoyer ${validFiles.length} facture(s) au traitement`}
        </button>
      )}
    </div>
  );
};

export default DropInvoicesPage;
